import { getDefaultClient } from '../../../../client/defaultClient';
import { NetworkSwitchingUnavailableError } from '../../../../errors/NetworkSwitchingUnavailableError';
import { getWalletProviderByKey } from '../../getWalletProviderByKey';
import type { WalletAccount } from '../../walletAccount';

type SwitchActiveNetworkParams = {
  networkId: string;
  walletAccount: WalletAccount;
};

/**
 * Switches the active network for a wallet account.
 *
 * This function programmatically changes the network
 * that the wallet is currently connected to.
 *
 * @param params.networkId - The identifier of the network to switch to. Should match the networkId of the network data.
 * @param params.walletAccount - The wallet account to switch networks for.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves when the network switch is complete.
 * @throws NetworkSwitchingUnavailableError If the wallet provider doesn't support network switching.
 */
export const switchActiveNetwork = async (
  { networkId, walletAccount }: SwitchActiveNetworkParams,
  client = getDefaultClient()
) => {
  const walletProvider = getWalletProviderByKey(
    { walletProviderKey: walletAccount.walletProviderKey },
    client
  );

  if (walletProvider.switchActiveNetwork === undefined) {
    throw new NetworkSwitchingUnavailableError({
      extraMessages: [
        `Wallet provider ${walletAccount.walletProviderKey} does not have switchActiveNetwork method`,
      ],
      originalError: null,
      walletProviderKey: walletAccount.walletProviderKey,
    });
  }

  return walletProvider.switchActiveNetwork({ networkId });
};
